"use client";

import { c as _c } from "react/compiler-runtime";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropId } from "@/generated/models/ApiDropId";
import type { Drop } from "@/helpers/waves/drop.helpers";
import useCapacitor from "@/hooks/useCapacitor";
import useDmWavesList from "@/hooks/useDmWavesList";
import useWavesList from "@/hooks/useWavesList";
import { WebSocketStatus } from "@/services/websocket/WebSocketTypes";
import { logWebSocketDebug } from "@/services/websocket/webSocketDebug";
import { useWebsocketStatus } from "@/services/websocket/useWebSocketMessage";
import type { ReactNode } from "react";
import React, { createContext, useCallback, useContext, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { WaveMessages } from "./hooks/types";
import { useActiveWaveManager } from "./hooks/useActiveWaveManager";
import type { MinimalWave } from "./hooks/useEnhancedWavesListCore";
import useEnhancedWavesListCore from "./hooks/useEnhancedWavesListCore";
import { useWaveDataManager } from "./hooks/useWaveDataManager";
import type { Listener as WaveMessagesListener } from "./hooks/useWaveMessagesStore";
import useWaveMessagesStore from "./hooks/useWaveMessagesStore";
import type { NextPageProps } from "./hooks/useWavePagination";
import type { ProcessIncomingDropType } from "./hooks/useWaveRealtimeUpdater";
import { useWaveRealtimeUpdater } from "./hooks/useWaveRealtimeUpdater";

// Define nested structures for context data
interface WavesContextData {
  readonly list: MinimalWave[];
  readonly isFetching: boolean;
  readonly isFetchingNextPage: boolean;
  readonly hasNextPage: boolean;
  readonly fetchNextPage: () => void;
  readonly addPinnedWave: (id: string) => void;
  readonly removePinnedWave: (id: string) => void;
  readonly restoreWaveUnreadCount: (waveId: string, count?: number) => void;
}
interface ActiveWaveSetOptions {
  readonly isDirectMessage?: boolean | undefined;
  readonly replace?: boolean | undefined;
  readonly serialNo?: number | string | null | undefined;
  readonly divider?: number | null | undefined;
}
interface ActiveWaveContextData {
  readonly id: string | null;
  readonly set: (waveId: string | null, options?: ActiveWaveSetOptions) => void;
}

// Define the interface for the wave messages store functions
interface WaveMessagesStoreData {
  readonly getData: (key: string) => WaveMessages | undefined;
  readonly subscribe: (key: string, listener: WaveMessagesListener) => void;
  readonly unsubscribe: (key: string, listener: WaveMessagesListener) => void;
}

// Define the type for our context using nested structures
interface MyStreamContextType {
  readonly waves: WavesContextData;
  readonly directMessages: WavesContextData;
  readonly activeWave: ActiveWaveContextData;
  readonly waveMessagesStore: WaveMessagesStoreData;
  readonly registerWave: (waveId: string, syncNewest?: boolean) => void;
  readonly fetchNextPageForWave: (props: NextPageProps) => Promise<(ApiDrop | ApiDropId)[] | null>;
  readonly fetchAroundSerialNo: (waveId: string, serialNo: number) => void;
  readonly processIncomingDrop: (drop: ApiDrop, type: ProcessIncomingDropType) => void;
  readonly processDropRemoved: (waveId: string, dropId: string) => void;
  readonly applyOptimisticDropUpdate: ({
    waveId,
    dropId,
    update
  }: {
    waveId: string;
    dropId: string;
    update: (draft: Drop) => Drop | void;
  }) => {
    rollback: () => void;
  } | null;
}
interface MyStreamProviderProps {
  readonly children: ReactNode;
}
const BROWSER_RESUME_SYNC_COOLDOWN_MS = 1000;
type StreamSyncSource = "browser-resume" | "websocket-connected" | "capacitor-resume";
type BrowserResumeSource = "visibilitychange" | "focus" | "online";

// Create the context
const MyStreamContext = createContext<MyStreamContextType | null>(null);

// Create a provider component
export const MyStreamProvider: React.FC<MyStreamProviderProps> = t0 => {
  const $ = _c(103);
  const {
    children
  } = t0;
  const {
    isCapacitor,
    isActive
  } = useCapacitor();
  const {
    activeWaveId,
    setActiveWave
  } = useActiveWaveManager();
  const mainWavesData = useWavesList();
  const dmWavesData = useDmWavesList();
  let t1;
  if ($[0] !== mainWavesData.waves) {
    t1 = new Set(mainWavesData.waves.map(_temp));
    $[0] = mainWavesData.waves;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mainWaveIds = t1;
  let t2;
  if ($[2] !== dmWavesData.waves) {
    t2 = new Set(dmWavesData.waves.map(_temp2));
    $[2] = dmWavesData.waves;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const dmWaveIds = t2;
  let t3;
  if ($[4] !== dmWaveIds) {
    t3 = {
      supportsPinning: true,
      otherListWaveIds: dmWaveIds
    };
    $[4] = dmWaveIds;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const wavesHookData = useEnhancedWavesListCore(activeWaveId, mainWavesData, t3);
  let t4;
  if ($[6] !== mainWaveIds) {
    t4 = {
      supportsPinning: false,
      otherListWaveIds: mainWaveIds
    };
    $[6] = mainWaveIds;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const dmWavesHookData = useEnhancedWavesListCore(activeWaveId, dmWavesData, t4);
  const waveMessagesStore = useWaveMessagesStore();
  const websocketStatus = useWebsocketStatus();
  const prevIsActiveRef = useRef(isActive);
  const lastBrowserResumeSyncAtRef = useRef(0);
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  let t5;
  if ($[8] !== waveMessagesStore.getData || $[9] !== waveMessagesStore.removeDrop || $[10] !== waveMessagesStore.updateData) {
    t5 = {
      updateData: waveMessagesStore.updateData,
      getData: waveMessagesStore.getData,
      removeDrop: waveMessagesStore.removeDrop
    };
    $[8] = waveMessagesStore.getData;
    $[9] = waveMessagesStore.removeDrop;
    $[10] = waveMessagesStore.updateData;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const waveDataManager = useWaveDataManager(t5);
  const {
    registerWave,
    syncNewestMessages,
    fetchNextPage,
    fetchAroundSerialNo
  } = waveDataManager;
  const refetchAllMainWaves = wavesHookData.refetchAllWaves;
  const refetchAllDmWaves = dmWavesHookData.refetchAllWaves;
  const resetAllMainWavesNewDropsCount = wavesHookData.resetAllWavesNewDropsCount;
  const resetAllDmWavesNewDropsCount = dmWavesHookData.resetAllWavesNewDropsCount;
  const wavesRef = useRef(wavesHookData.waves);
  const dmWavesRef = useRef(dmWavesHookData.waves);
  let t6;
  let t7;
  if ($[12] !== dmWavesHookData.waves || $[13] !== wavesHookData.waves) {
    t6 = () => {
      wavesRef.current = wavesHookData.waves;
      dmWavesRef.current = dmWavesHookData.waves;
    };
    t7 = [wavesHookData.waves, dmWavesHookData.waves];
    $[12] = dmWavesHookData.waves;
    $[13] = wavesHookData.waves;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useLayoutEffect(t6, t7);
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = waveId => {
      const wave_1 = wavesRef.current.find(w => w.id === waveId);
      if (wave_1) {
        return wave_1.isMuted;
      }
      const dmWave = dmWavesRef.current.find(w_0 => w_0.id === waveId);
      if (dmWave) {
        return dmWave.isMuted;
      }
      return false;
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const isWaveMuted = t8;
  let t9;
  if ($[17] !== activeWaveId || $[18] !== registerWave || $[19] !== removeWaveDeliveredNotifications || $[20] !== syncNewestMessages || $[21] !== waveMessagesStore.getData || $[22] !== waveMessagesStore.removeDrop || $[23] !== waveMessagesStore.updateData) {
    t9 = {
      activeWaveId,
      getData: waveMessagesStore.getData,
      updateData: waveMessagesStore.updateData,
      registerWave,
      syncNewestMessages,
      removeDrop: waveMessagesStore.removeDrop,
      removeWaveDeliveredNotifications,
      isWaveMuted
    };
    $[17] = activeWaveId;
    $[18] = registerWave;
    $[19] = removeWaveDeliveredNotifications;
    $[20] = syncNewestMessages;
    $[21] = waveMessagesStore.getData;
    $[22] = waveMessagesStore.removeDrop;
    $[23] = waveMessagesStore.updateData;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const {
    processIncomingDrop,
    processDropRemoved
  } = useWaveRealtimeUpdater(t9);
  let t10;
  if ($[25] !== registerWave || $[26] !== setActiveWave) {
    t10 = (waveId_0, options) => {
      if (waveId_0) {
        registerWave(waveId_0, true);
      }
      setActiveWave(waveId_0, options);
    };
    $[25] = registerWave;
    $[26] = setActiveWave;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const setActiveWaveAndRegister = t10;
  let t11;
  if ($[28] !== activeWaveId || $[29] !== refetchAllDmWaves || $[30] !== refetchAllMainWaves || $[31] !== registerWave) {
    t11 = source => {
      logWebSocketDebug("Running stream sync", {
        source,
        hasActiveWave: Boolean(activeWaveId)
      });
      if (activeWaveId) {
        registerWave(activeWaveId, true);
      }
      refetchAllMainWaves();
      refetchAllDmWaves();
    };
    $[28] = activeWaveId;
    $[29] = refetchAllDmWaves;
    $[30] = refetchAllMainWaves;
    $[31] = registerWave;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const syncActiveWaveAndRefetch = useEffectEvent(t11);
  let t12;
  if ($[33] !== activeWaveId || $[34] !== syncActiveWaveAndRefetch) {
    t12 = source_0 => {
      if (document.visibilityState !== "visible") {
        logWebSocketDebug("Browser resume sync skipped because document is hidden", {
          source: source_0,
          visibilityState: document.visibilityState
        });
        return;
      }
      const now = Date.now();
      const sinceLastBrowserResumeSyncMs = now - lastBrowserResumeSyncAtRef.current;
      if (sinceLastBrowserResumeSyncMs < BROWSER_RESUME_SYNC_COOLDOWN_MS) {
        logWebSocketDebug("Browser resume sync suppressed by cooldown", {
          source: source_0,
          sinceLastBrowserResumeSyncMs,
          cooldownMs: BROWSER_RESUME_SYNC_COOLDOWN_MS
        });
        return;
      }
      lastBrowserResumeSyncAtRef.current = now;
      logWebSocketDebug("Browser resume sync triggered", {
        source: source_0,
        hasActiveWave: Boolean(activeWaveId)
      });
      syncActiveWaveAndRefetch("browser-resume");
    };
    $[33] = activeWaveId;
    $[34] = syncActiveWaveAndRefetch;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const runBrowserResumeSync = useEffectEvent(t12);
  let t13;
  if ($[36] !== activeWaveId || $[37] !== isCapacitor || $[38] !== resetAllDmWavesNewDropsCount || $[39] !== resetAllMainWavesNewDropsCount || $[40] !== syncActiveWaveAndRefetch) {
    t13 = () => {
      logWebSocketDebug("WebSocket connected; triggering stream sync", {
        hasActiveWave: Boolean(activeWaveId),
        isCapacitor
      });
      syncActiveWaveAndRefetch("websocket-connected");
      if (isCapacitor) {
        logWebSocketDebug("Resetting new drop counts after websocket reconnect on capacitor");
        resetAllMainWavesNewDropsCount();
        resetAllDmWavesNewDropsCount();
      }
    };
    $[36] = activeWaveId;
    $[37] = isCapacitor;
    $[38] = resetAllDmWavesNewDropsCount;
    $[39] = resetAllMainWavesNewDropsCount;
    $[40] = syncActiveWaveAndRefetch;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const handleConnectedWebSocket = useEffectEvent(t13);
  let t14;
  if ($[42] !== activeWaveId || $[43] !== resetAllDmWavesNewDropsCount || $[44] !== resetAllMainWavesNewDropsCount || $[45] !== syncActiveWaveAndRefetch) {
    t14 = () => {
      logWebSocketDebug("Capacitor app resumed; triggering stream sync", {
        hasActiveWave: Boolean(activeWaveId)
      });
      syncActiveWaveAndRefetch("capacitor-resume");
      resetAllMainWavesNewDropsCount();
      resetAllDmWavesNewDropsCount();
    };
    $[42] = activeWaveId;
    $[43] = resetAllDmWavesNewDropsCount;
    $[44] = resetAllMainWavesNewDropsCount;
    $[45] = syncActiveWaveAndRefetch;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  const handleCapacitorResume = useEffectEvent(t14);
  let t15;
  if ($[47] !== handleConnectedWebSocket || $[48] !== websocketStatus) {
    t15 = () => {
      if (websocketStatus !== WebSocketStatus.CONNECTED) {
        return;
      }
      handleConnectedWebSocket();
    };
    $[47] = handleConnectedWebSocket;
    $[48] = websocketStatus;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== websocketStatus) {
    t16 = [websocketStatus];
    $[50] = websocketStatus;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  useEffect(t15, t16);
  let t17;
  if ($[52] !== handleCapacitorResume || $[53] !== isActive || $[54] !== isCapacitor) {
    t17 = () => {
      if (!isCapacitor) {
        return;
      }
      if (!prevIsActiveRef.current && isActive) {
        handleCapacitorResume();
      }
      prevIsActiveRef.current = isActive;
    };
    $[52] = handleCapacitorResume;
    $[53] = isActive;
    $[54] = isCapacitor;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== isActive || $[57] !== isCapacitor) {
    t18 = [isActive, isCapacitor];
    $[56] = isActive;
    $[57] = isCapacitor;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  useEffect(t17, t18);
  let t19;
  if ($[59] !== isCapacitor || $[60] !== runBrowserResumeSync) {
    t19 = () => {
      if (isCapacitor) {
        return;
      }
      const handleVisibilityChange = () => {
        if (document.visibilityState !== "visible") {
          return;
        }
        runBrowserResumeSync("visibilitychange");
      };
      const handleFocus = () => {
        runBrowserResumeSync("focus");
      };
      const handleOnline = () => {
        runBrowserResumeSync("online");
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("focus", handleFocus);
      window.addEventListener("online", handleOnline);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("focus", handleFocus);
        window.removeEventListener("online", handleOnline);
      };
    };
    $[59] = isCapacitor;
    $[60] = runBrowserResumeSync;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== isCapacitor) {
    t20 = [isCapacitor];
    $[62] = isCapacitor;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  useEffect(t19, t20);
  let t21;
  if ($[64] !== wavesHookData.addPinnedWave || $[65] !== wavesHookData.fetchNextPage || $[66] !== wavesHookData.hasNextPage || $[67] !== wavesHookData.isFetching || $[68] !== wavesHookData.isFetchingNextPage || $[69] !== wavesHookData.removePinnedWave || $[70] !== wavesHookData.restoreWaveUnreadCount || $[71] !== wavesHookData.waves) {
    t21 = {
      list: wavesHookData.waves,
      isFetching: wavesHookData.isFetching,
      isFetchingNextPage: wavesHookData.isFetchingNextPage,
      hasNextPage: wavesHookData.hasNextPage,
      fetchNextPage: wavesHookData.fetchNextPage,
      addPinnedWave: wavesHookData.addPinnedWave,
      removePinnedWave: wavesHookData.removePinnedWave,
      restoreWaveUnreadCount: wavesHookData.restoreWaveUnreadCount
    };
    $[64] = wavesHookData.addPinnedWave;
    $[65] = wavesHookData.fetchNextPage;
    $[66] = wavesHookData.hasNextPage;
    $[67] = wavesHookData.isFetching;
    $[68] = wavesHookData.isFetchingNextPage;
    $[69] = wavesHookData.removePinnedWave;
    $[70] = wavesHookData.restoreWaveUnreadCount;
    $[71] = wavesHookData.waves;
    $[72] = t21;
  } else {
    t21 = $[72];
  }
  const waves = t21;
  let t22;
  if ($[73] !== dmWavesHookData.addPinnedWave || $[74] !== dmWavesHookData.fetchNextPage || $[75] !== dmWavesHookData.hasNextPage || $[76] !== dmWavesHookData.isFetching || $[77] !== dmWavesHookData.isFetchingNextPage || $[78] !== dmWavesHookData.removePinnedWave || $[79] !== dmWavesHookData.restoreWaveUnreadCount || $[80] !== dmWavesHookData.waves) {
    t22 = {
      list: dmWavesHookData.waves,
      isFetching: dmWavesHookData.isFetching,
      isFetchingNextPage: dmWavesHookData.isFetchingNextPage,
      hasNextPage: dmWavesHookData.hasNextPage,
      fetchNextPage: dmWavesHookData.fetchNextPage,
      addPinnedWave: dmWavesHookData.addPinnedWave,
      removePinnedWave: dmWavesHookData.removePinnedWave,
      restoreWaveUnreadCount: dmWavesHookData.restoreWaveUnreadCount
    };
    $[73] = dmWavesHookData.addPinnedWave;
    $[74] = dmWavesHookData.fetchNextPage;
    $[75] = dmWavesHookData.hasNextPage;
    $[76] = dmWavesHookData.isFetching;
    $[77] = dmWavesHookData.isFetchingNextPage;
    $[78] = dmWavesHookData.removePinnedWave;
    $[79] = dmWavesHookData.restoreWaveUnreadCount;
    $[80] = dmWavesHookData.waves;
    $[81] = t22;
  } else {
    t22 = $[81];
  }
  const directMessages = t22;
  let t23;
  if ($[82] !== activeWaveId || $[83] !== setActiveWaveAndRegister) {
    t23 = {
      id: activeWaveId,
      set: setActiveWaveAndRegister
    };
    $[82] = activeWaveId;
    $[83] = setActiveWaveAndRegister;
    $[84] = t23;
  } else {
    t23 = $[84];
  }
  const activeWave = t23;
  let t24;
  if ($[85] !== waveMessagesStore.getData || $[86] !== waveMessagesStore.subscribe || $[87] !== waveMessagesStore.unsubscribe) {
    t24 = {
      getData: waveMessagesStore.getData,
      subscribe: waveMessagesStore.subscribe,
      unsubscribe: waveMessagesStore.unsubscribe
    };
    $[85] = waveMessagesStore.getData;
    $[86] = waveMessagesStore.subscribe;
    $[87] = waveMessagesStore.unsubscribe;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  const waveMessagesStoreData = t24;
  let t25;
  if ($[89] !== activeWave || $[90] !== directMessages || $[91] !== fetchAroundSerialNo || $[92] !== fetchNextPage || $[93] !== processDropRemoved || $[94] !== processIncomingDrop || $[95] !== registerWave || $[96] !== waveMessagesStore.optimisticUpdateDrop || $[97] !== waveMessagesStoreData || $[98] !== waves) {
    t25 = {
      waves,
      directMessages,
      activeWave,
      waveMessagesStore: waveMessagesStoreData,
      registerWave,
      fetchNextPageForWave: fetchNextPage,
      fetchAroundSerialNo,
      processIncomingDrop,
      processDropRemoved,
      applyOptimisticDropUpdate: waveMessagesStore.optimisticUpdateDrop
    };
    $[89] = activeWave;
    $[90] = directMessages;
    $[91] = fetchAroundSerialNo;
    $[92] = fetchNextPage;
    $[93] = processDropRemoved;
    $[94] = processIncomingDrop;
    $[95] = registerWave;
    $[96] = waveMessagesStore.optimisticUpdateDrop;
    $[97] = waveMessagesStoreData;
    $[98] = waves;
    $[99] = t25;
  } else {
    t25 = $[99];
  }
  const contextValue = t25;
  let t26;
  if ($[100] !== children || $[101] !== contextValue) {
    t26 = <MyStreamContext.Provider value={contextValue}>{children}</MyStreamContext.Provider>;
    $[100] = children;
    $[101] = contextValue;
    $[102] = t26;
  } else {
    t26 = $[102];
  }
  return t26;
};

// Create a custom hook for using this context
export const useMyStream = () => {
  const context = useContext(MyStreamContext);
  if (!context) {
    throw new Error("useMyStream must be used within a MyStreamProvider");
  }
  return context;
};
export const useMyStreamOptional = () => {
  return useContext(MyStreamContext);
};

// Create the selector hook for wave messages
export function useMyStreamWaveMessages(waveId) {
  const $ = _c(12);
  const {
    waveMessagesStore
  } = useMyStream();
  const {
    getData,
    subscribe,
    unsubscribe
  } = waveMessagesStore;
  let t0;
  if ($[0] !== getData || $[1] !== waveId) {
    t0 = () => waveId ? getData(waveId) : undefined;
    $[0] = getData;
    $[1] = waveId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [data, setData] = useState(t0);
  let t1;
  let t2;
  if ($[3] !== subscribe || $[4] !== unsubscribe || $[5] !== waveId) {
    t1 = () => {
      if (!waveId) {
        setData(undefined);
        return;
      }
      const listener = newData => {
        setData(currentData => {
          if (JSON.stringify(currentData) !== JSON.stringify(newData)) {
            return newData;
          }
          return currentData;
        });
      };
      subscribe(waveId, listener);
      return () => {
        unsubscribe(waveId, listener);
      };
    };
    t2 = [waveId, subscribe, unsubscribe];
    $[3] = subscribe;
    $[4] = unsubscribe;
    $[5] = waveId;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[8] !== getData || $[9] !== waveId) {
    t3 = () => {
      setData(waveId ? getData(waveId) : undefined);
    };
    t4 = [waveId, getData];
    $[8] = getData;
    $[9] = waveId;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  return data;
}
function _temp(wave) {
  return wave.id;
}
function _temp2(wave_0) {
  return wave_0.id;
}